import config from "./config";

const AIEndpoint = config.AIBackendUrl;
const GeneralEndpoint = config.GeneralBackendUrl;
const DialogEndpoint = config.DialogBackendUrl;

// Common function to create request body
const createRequestBody = (
  property_id,
  conversation_id,
  content,
  source,
  mode,
  document,
  sender,
  recepient,
  no_search,
  file_ids = []
) => {
  let requestBody = {
    message: {
      role: "user",
      content,
    },
  };

  if (property_id) {
    requestBody.context = { property_id };
  }
  if (conversation_id) {
    requestBody.conversation_id = conversation_id;
  }
  if (document) {
    requestBody.document = document;
  }
  if (source) {
    requestBody.source = source;
  }
  if (mode) {
    requestBody.mode = mode;
  }
  if (sender) {
    requestBody.sender = sender;
  }
  if (recepient) {
    requestBody.recepient = recepient;
  }
  requestBody.no_search = no_search;
  requestBody.file_ids = Array.isArray(file_ids) ? file_ids : [];

  // Note: Do not include style in message payload; backend expects only role and content


  // add local time to the message
  const now = new Date();
  const formattedDate = now.toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const hours = now.getHours();
  let periodOfDay;
  if (hours >= 0 && hours < 12) {
    periodOfDay = "in the morning";
  } else if (hours >= 12 && hours < 17) {
    periodOfDay = "in the afternoon";
  } else {
    periodOfDay = "in the evening";
  }

  requestBody.local_date = `${formattedDate} ${periodOfDay}`;

  return requestBody;
};

const fetchMessageStatus = async (message_id, authToken) => {
  const statusResponse = await fetch(`${AIEndpoint}message/status/get?message_id=${message_id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
      "X-Data-Source": config.DataSource,
    },
  });
  const statusData = await statusResponse.json();
  try {
    console.log("pollUserMessage: message/status/get response:", statusData);
  } catch (e) {
    // ignore logging errors
  }
  return statusData;
};

const handlePollStatus = (statusData, onProgress) => {
  if (statusData.status === "SUCCESS") {
    return { done: true, result: statusData.result };
  }
  if (statusData.status === "FAILURE") {
    console.error(statusData);
    throw new Error("Message processing failed");
  }
  if (statusData.status === "PENDING" || statusData.status === "PROGRESS") {
    onProgress?.(statusData);
  }
  return { done: false };
};

export const pollUserMessage = async (message_id, authToken, docSearch = false, onProgress) => {
  const timeout = docSearch ? 240000 : 140000; // 4 minutes with doc search, ~2m 20s otherwise
  const stage1Interval = 500; // 500ms for stage 1
  const stage1MaxChecks = 20; // 20 checks at 500ms
  const stage2Interval = 1000; // 1s for stage 2
  let elapsedTime = 0;

  try {
    console.log("pollUserMessage: starting to poll message/status/get for message_id:", message_id);
  } catch (e) {
    // ignore logging errors
  }

  // Stage 1: fast polling
  for (let i = 0; i < stage1MaxChecks && elapsedTime < timeout; i++) {
    const statusData = await fetchMessageStatus(message_id, authToken);
    const pollResult = handlePollStatus(statusData, onProgress);
    if (pollResult.done) {
      return pollResult.result;
    }
    await new Promise((resolve) => setTimeout(resolve, stage1Interval));
    elapsedTime += stage1Interval;
  }

  // Stage 2: slower polling until timeout
  while (elapsedTime < timeout) {
    const statusData = await fetchMessageStatus(message_id, authToken);
    const pollResult = handlePollStatus(statusData, onProgress);
    if (pollResult.done) {
      return pollResult.result;
    }
    await new Promise((resolve) => setTimeout(resolve, stage2Interval));
    elapsedTime += stage2Interval;
  }
  throw new Error("Timeout waiting for message to process");
};

export const fetchInitialData = async (fromEmails, toEmail, authToken, documentContent = "") => {
  try {
    // since we are sending emails in the body, we do not need to encode them
    // const encodedFromEmails = fromEmails.map(email => encodeURIComponent(email));
    // const encodedToEmail = encodeURIComponent(toEmail);
    const body = { recepient_email: toEmail, emails: fromEmails, document_content: documentContent };
    console.log("fetchInitialData", body);
    const response = await fetch(`${AIEndpoint}context_0`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network error: " + response.statusText + "(" + response.status + ")");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch initial chat data:", error);
    throw error;
  }
};
export const fetchResources = async (authToken, propertyId = 0) => {
  try {
    const response = await fetch(
      `${GeneralEndpoint}resource?not_succeeded=false&property_id=${propertyId || 0}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          "X-Data-Source": config.DataSource,
        },
      }
    );
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch resources:", error);
    throw error;
  }
};

export const fetchFolders = async (authToken, propertyId = 0) => {
  try {
    const response = await fetch(
      `${GeneralEndpoint}folder?property_id=${propertyId || 0}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          "X-Data-Source": config.DataSource,
        },
      }
    );
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch folders:", error);
    throw error;
  }
};

export const uploadPropertyFiles = async (authToken, propertyId, folderId, files) => {
  try {
    const response = await fetch(`${GeneralEndpoint}property/upload/${propertyId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify({
        property_id: propertyId,
        folder_id: folderId,
        files,
      }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      let message = "Upload failed";
      try {
        const errorData = await response.json();
        message = errorData?.message || errorData?.error || message;
      } catch {
        // ignore parse errors
      }
      throw new Error(message);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to upload files:", error);
    throw error;
  }
};

export const fetchProperties = async (authToken) => {
  try {
    const response = await fetch(`${GeneralEndpoint}property/user/assignments`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch properties:", error);
    throw error;
  }
};

export const fetchConversationMessages = async (conversation_id, authToken) => {
  try {
    const response = await fetch(`${AIEndpoint}message/by_conversation/${conversation_id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch conversation messages:", error);
    throw error;
  }
};

export const postUserMessage = async (
  property_id,
  conversation_id,
  content,
  authToken,
  source,
  mode,
  document,
  sender,
  recepient,
  no_search,
  file_ids = [],
  onProgress
) => {
  try {
    console.log("postUserMessage called with:", {
      property_id,
      conversation_id,
      content,
      authToken,
      source,
      mode,
      document,
      sender,
      recepient,
      no_search,
      file_ids,
    });
    const requestBody = createRequestBody(
      property_id,
      conversation_id,
      content,
      source,
      mode,
      document,
      sender,
      recepient,
      no_search,
      file_ids
    );
    console.log(requestBody);
    const url = `${AIEndpoint}message/enqueue`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      console.log("Server response to our request:", response);
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    console.log("Server response to our request:", data);

    // Determine if document search is enabled based on the request arg 'no_search'
    const docSearch = !no_search;
    return await pollUserMessage(data.id, authToken, docSearch, onProgress);
  } catch (error) {
    console.error("Failed to post message:", error);
    throw error;
  }
};

export const submitUserMessage = async (
  property_id,
  conversation_id,
  content,
  authToken,
  source,
  mode,
  document,
  sender,
  recepient,
  no_search,
  file_ids = []
) => {
  try {
    const requestBody = createRequestBody(
      property_id,
      conversation_id,
      content,
      source,
      mode,
      document,
      sender,
      recepient,
      no_search,
      file_ids
    );
    console.log(requestBody);
    const url = `${AIEndpoint}message/enqueue`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      console.log("Server response to our request:", response);
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    console.log("Server response to our request:", data);
    return data;
  } catch (error) {
    console.error("Failed to submit message:", error);
    throw error;
  }
};

export const postFeedback = async (messageId, feedbackMessage, authToken) => {
  try {
    const url = `${AIEndpoint}message/${messageId}`;

    let requestBody = {
      user_feedback: feedbackMessage,
    };

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to post feedback:", error);
    throw error;
  }
};

export const postUsage = async (activity, category, meta, authToken) => {
  try {
    const url = `${GeneralEndpoint}usage`;

    let requestBody = {
      activity,
      category,
      meta,
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to post usage:", error);
    throw error;
  }
};

export const createDialogSession = async (authToken) => {
  try {
    const url = `${DialogEndpoint}dialog`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to create dialog session:", error);
    throw error;
  }
};

export const fetchDialogSession = async (session_id) => {
  const response = await fetch(`${DialogEndpoint}dialog?session_id=${session_id}`, {
    method: "GET",
    headers: {
      "X-Data-Source": config.DataSource,
    },
  });
  const data = await response.json();
  const formattedItems = data.map((item) => ({
    key: item.id.toString(),
    text: `${item.name} (${item.legal_des})`,
  }));
  return formattedItems;
};

// Request a file download URL from XANO by passing reference link data
export const requestAttachmentByReference = async (reference, authToken) => {
  try {
    const url = `${GeneralEndpoint}reference/resolve`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify({ reference }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    // Graph response via XANO backend uses '@microsoft.graph.downloadUrl' and 'name'
    const downloadUrl = data["@microsoft.graph.downloadUrl"] || data.download_url;
    const filename = data.name || data.filename || reference?.name || "attachment";
    if (!downloadUrl) {
      throw new Error("Missing download URL in response");
    }
    return { download_url: downloadUrl, filename };
  } catch (error) {
    console.error("Failed to request attachment by reference:", error);
    throw error;
  }
};

// Request a shareable link for a reference and return its webUrl
export const requestShareableLinkByReference = async (reference, authToken) => {
  try {
    const url = `${GeneralEndpoint}reference/resolve`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Data-Source": config.DataSource,
      },
      body: JSON.stringify({ reference }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized");
      }
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    const webUrl = (data.shareableLink && data.shareableLink.webUrl);
    if (!webUrl) {
      throw new Error("Missing shareable link in response");
    }
    return { webUrl };
  } catch (error) {
    console.error("Failed to request shareable link by reference:", error);
    throw error;
  }
};